// API Security · Threat Intel — activity match side drawer (v3 shell).

import React from "react";
import PropTypes from "prop-types";
import { Stack, Box, Typography, Chip } from "@mui/material";
import { Activity, Shield, ShieldX, Eye } from "lucide-react";
import copy from "copy-to-clipboard";
import colors from "../../../../../../utils/colors";
import LayerdIcon from "../../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../../icons/detectionLucideIcons";
import {
  ASEC_ENABLED_TAG,
  ASEC_LEARN_TAG,
  asecEnabledTagChipSx,
  asecLearnTagChipSx,
} from "../asecUi";
import WafEventDrawerShell from "../../../detection/WafEventDrawerShell";
import FindingDrawerHeader from "../../../detection/v3/FindingDrawerHeader";
import SmartParseRawJsonViewer from "../../../detection/v3/SmartParseRawJsonViewer";
import {
  SmartParseSectionCard as SectionCard,
  DrawerFieldStack,
  spAssessLabelSx,
} from "../../../detection/v3/smartParseDrawerUi";
import { TEXT_PRIMARY } from "../../../detection/wafEventDrawerShared";

const DANGER_INK = colors.red[200];
const DANGER_BG = colors.red[10];
const DANGER_BORDER = "rgba(251, 55, 72, 0.30)";
const SHADOW_INK = "#C29021";
const SHADOW_BG = colors.secondary[10];
const SHADOW_BORDER = "rgba(242, 180, 41, 0.35)";

const statusChipBaseSx = {
  height: "auto",
  minHeight: 24,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.01em",
  borderRadius: "999px",
  "& .MuiChip-label": { px: 1.25, py: 0.375, lineHeight: 1.3 },
  "& .MuiChip-icon": { ml: 0.875, color: "inherit" },
};

function StatusHighlightCell({ label, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.875,
        px: 1.25,
        py: 1.125,
        borderRadius: "8px",
        border: `1px solid ${colors.neutral[200]}`,
        backgroundColor: colors.neutral[50],
        minWidth: 0,
      }}
    >
      <Typography sx={spAssessLabelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

function MatchVerdictChip({ verdict }) {
  const isBlock = verdict === "block";
  const conf = isBlock
    ? { icon: ShieldX, label: "403 Blocked", bg: DANGER_BG, fg: DANGER_INK, bd: DANGER_BORDER }
    : { icon: Eye, label: "Shadow", bg: SHADOW_BG, fg: SHADOW_INK, bd: SHADOW_BORDER };
  return (
    <Chip
      size="small"
      icon={<LayerdIcon icon={conf.icon} size={13} color={conf.fg} />}
      label={conf.label}
      sx={{
        ...statusChipBaseSx,
        backgroundColor: conf.bg,
        color: conf.fg,
        border: `1px solid ${conf.bd}`,
      }}
    />
  );
}

function MatchStateChip({ state }) {
  const isEnforce = state === "enforce";
  const isLearn = state === "learn";
  if (isEnforce) {
    return (
      <Chip
        size="small"
        icon={<LayerdIcon icon={Shield} size={13} color={ASEC_ENABLED_TAG.fg} />}
        label="Enforce"
        sx={{
          ...statusChipBaseSx,
          ...asecEnabledTagChipSx,
        }}
      />
    );
  }
  if (isLearn) {
    return (
      <Chip
        size="small"
        icon={<LayerdIcon icon={Eye} size={13} color={ASEC_LEARN_TAG.fg} />}
        label="Learn"
        sx={{
          ...statusChipBaseSx,
          ...asecLearnTagChipSx,
        }}
      />
    );
  }
  return (
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.neutral[600] }}>
      {state || "—"}
    </Typography>
  );
}

export default function ThreatIntelActivityDrawer({ open, onClose, row }) {
  const [snackOpen, setSnackOpen] = React.useState(false);

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  if (!row) {
    return (
      <WafEventDrawerShell open={false} onClose={onClose} title="Threat intel match">
        {null}
      </WafEventDrawerShell>
    );
  }

  return (
    <>
      <WafEventDrawerShell
        open={open}
        onClose={onClose}
        title="Threat intel match"
        headerVariant="hero"
        headerContent={(
          <FindingDrawerHeader
            embedded
            title="Threat intel match"
            method={row.method}
            path={row.path}
            icon={Activity}
          />
        )}
      >
        <SectionCard icon={DETECTION_ICONS.detection} title="Match summary">
          <Stack spacing={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
              }}
            >
              <StatusHighlightCell label="Verdict">
                <MatchVerdictChip verdict={row.verdict} />
              </StatusHighlightCell>
              <StatusHighlightCell label="Matched state">
                <MatchStateChip state={row.state} />
              </StatusHighlightCell>
            </Box>
            <DrawerFieldStack
              columns={2}
              onCopy={handleCopy}
              fields={[
                {
                  key: "timestamp",
                  label: "Time",
                  value: row.time,
                },
                row.since
                  ? { key: "relative", label: "Relative", value: row.since }
                  : null,
                {
                  key: "sourceIp",
                  label: "Source IP",
                  value: row.ip,
                  copyable: !!row.ip,
                  monospace: true,
                },
                {
                  key: "id",
                  label: "ID",
                  value: row.id,
                  copyable: !!row.id,
                  monospace: true,
                },
              ]}
            />
          </Stack>
        </SectionCard>

        <SectionCard icon={DETECTION_ICONS.request} title="Path hit">
          <DrawerFieldStack
            columns={2}
            onCopy={handleCopy}
            fields={[
              {
                key: "path",
                label: "Path",
                value: row.path,
                copyable: true,
                fullRow: true,
              },
              {
                key: "suspicious",
                label: "Suspicious path",
                value: row.suspicious ? "Yes" : "No",
              },
            ]}
          />
        </SectionCard>

        <SectionCard icon={DETECTION_ICONS.info} title="Raw data" defaultExpanded={false}>
          <SmartParseRawJsonViewer
            data={row}
            onCopy={handleCopy}
            filename="threat_intel_match.json"
          />
        </SectionCard>
      </WafEventDrawerShell>

      {snackOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            backgroundColor: TEXT_PRIMARY,
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1400,
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#fff" }}>
            Copied to clipboard!
          </Typography>
        </Box>
      )}
    </>
  );
}

ThreatIntelActivityDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  row: PropTypes.object,
};
